import { WSPillProps } from "@wingspanhq/fe-component-library";
import { InvoiceRow, InvoiceStatus } from "@wingspanhq/wingspan-api-sdk";

/**
 * Status pill for an invoice row, keyed directly on the SDK's raw
 * `InvoiceStatus` — no FE-derived status.
 *
 * Every member of `InvoiceStatus` is mapped below, so the two fallbacks are
 * distinct failure modes and read differently to the user (PLA-3538):
 * - `InvoiceRow.status` is optional and the row came back without one — the
 *   status is unavailable, which is not the same as unknown.
 * - a value outside the enum the app was built against (a status the API added
 *   since) — genuinely unrecognised.
 */
export const getInvoiceStatusPillProps = (invoice: InvoiceRow): WSPillProps => {
  const status = invoice.status;

  if (!status) {
    return {
      theme: "neutral",
      icon: "info-circle",
      text: "Status unavailable"
    };
  }

  switch (status) {
    case InvoiceStatus.CANCELLED:
      return { theme: "neutral", text: "Cancelled" };

    case InvoiceStatus.CREATED:
      return { theme: "neutral", icon: "draft", text: "Draft" };

    case InvoiceStatus.PENDING:
    case InvoiceStatus.DISPUTED:
    case InvoiceStatus.RETURNED:
      return { theme: "warning", icon: true, text: "Action required" };

    case InvoiceStatus.OPENED:
    case InvoiceStatus.OVERDUE:
      return { theme: "neutral", icon: "time", text: "Awaiting client" };

    case InvoiceStatus.PAYMENT_IN_TRANSIT:
      return { theme: "blue", icon: "send-mail", text: "In-progress" };

    case InvoiceStatus.PAID_OFF_PLATFORM:
      return { theme: "neutral", icon: "paperclip", text: "Off-platform" };

    case InvoiceStatus.PAID:
    case InvoiceStatus.PARTIALLY_PAID:
      return { theme: "success", icon: true, text: "Deposited" };

    case InvoiceStatus.REFUNDED:
    case InvoiceStatus.PARTIALLY_REFUNDED:
      return { theme: "neutral", icon: "undo", text: "Refunded" };

    default:
      return { theme: "warning", icon: true, text: "Unrecognized status" };
  }
};
